import { Link, useNavigate } from "react-router-dom";
import { useDocumentTitle } from "../../../functions/useDocumentTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import aimmaPatientContent from "../../../data/content/health/aimmaPatientContent.json";
import { DataKeys } from "../../../types";
import { useLang } from "../../Context";
import { NavTree } from "../../navigation/NavLinkPages";
import { HealthFaqItem } from "../HealthFaqItem";
import { HealthIntegrationEcosystemSection } from "../aimmaDoctor/HealthIntegrationEcosystemSection";
import { HealthSection } from "../HealthSection";
import { HealthSectionHeader } from "../HealthSectionHeader";
import {
    getLocalized,
    HealthLang,
    LocalizedString,
} from "../healthTypes";
import {
    healthAimmaPatientHeroSurface,
    healthAimmaDoctorSectionContentOffset,
    healthAimmaDoctorHeroCopyForeground,
    healthChildPageHeroBackdropGrid,
    healthChildPageHeroBackdropSectionY,
    healthChildPageHeroCopyStack,
    healthCol,
    healthContentPadX,
    healthSectionInnerStyle,
    healthSectionOuter,
} from "../healthLayout";
import {
    HEALTH_CTA_BAND_BG,
    healthChildPageLead,
    healthChildPageTitle,
    healthChildPageHeroBottomClipDesktop,
    healthCtaBand,
    healthCtaButton,
    healthCtaSecondaryButton,
    healthCtaSubtitle,
    healthCtaTitle,
    healthFocusVisible,
    healthPrimaryButton,
    healthProductLineLabel,
    healthSecondaryButton,
    healthButtonArrowFa,
    healthButtonLabel,
} from "../healthVisualStyles";
import { healthLocalePath } from "../aimmaDoctor/healthLocalePath";
import { scrollToHealthHashAnchor } from "../healthScroll";
import {
    HealthChildPageHeroBackdropLayer,
} from "./HealthChildPageHeroBackdrop";
import { HealthWhyPatientSection } from "./HealthWhyPatientSection";
import { HealthPatientJourneySection } from "./HealthPatientJourneySection";
import { HealthPatientModulesSection } from "./HealthPatientModulesSection";

const pageData = aimmaPatientContent as DataKeys;

function localePath(path: string, lang: HealthLang): string {
    return healthLocalePath(path, lang);
}

export function AimmaPatientPage() {
    const { lang } = useLang();
    const navigate = useNavigate();
    const hLang = lang as HealthLang;

    useDocumentTitle(getLocalized(pageData.pageTitle, hLang));

    const hero = pageData.hero as DataKeys;
    const whyPatient = pageData.whyPatient as DataKeys;
    const patientJourney = pageData.patientJourney as DataKeys;
    const modules = pageData.modules as DataKeys;
    const integration = pageData.integration as DataKeys;
    const faq = pageData.faq as DataKeys;
    const cta = pageData.cta as DataKeys;
    const patientCareNoteLabel = getLocalized(
        pageData.patientCareNoteLabel as LocalizedString,
        hLang
    );
    const clinicalInputsLabel = getLocalized(
        pageData.clinicalInputsLabel as LocalizedString,
        hLang
    );
    const outputsLabel = getLocalized(
        pageData.outputsLabel as LocalizedString,
        hLang
    );
    const workflowBenefitLabel = getLocalized(
        pageData.workflowBenefitLabel as LocalizedString,
        hLang
    );

    const moduleTitleById: Record<string, string> = {};
    (modules.blocks as DataKeys[]).forEach((block) => {
        moduleTitleById[block.id as string] = getLocalized(
            block.title as LocalizedString,
            hLang
        );
    });

    const heroCtaPrimary = hero.ctaPrimary as DataKeys;
    const heroCtaSecondary = hero.ctaSecondary as DataKeys;

    return (
        <main className="overflow-x-clip text-left bg-white">
            <header
                className={`${healthSectionOuter} ${healthAimmaPatientHeroSurface}`}
            >
                <HealthChildPageHeroBackdropLayer
                    visual={hero.visual as DataKeys}
                    lang={hLang}
                />
                <div
                    className={healthChildPageHeroBottomClipDesktop}
                    aria-hidden="true"
                />
                <div
                    className="relative z-[1] m-auto w-full"
                    style={healthSectionInnerStyle}
                >
                    <div
                        className={`${healthContentPadX} ${healthChildPageHeroBackdropSectionY}`}
                    >
                        <div className={healthChildPageHeroBackdropGrid}>
                            <p
                                className={`${healthProductLineLabel} ${healthAimmaDoctorHeroCopyForeground}`}
                            >
                                {getLocalized(hero.productLineLabel, hLang)}
                            </p>
                            <div
                                className={`${healthCol} ${healthChildPageHeroCopyStack} ${healthAimmaDoctorHeroCopyForeground}`}
                            >
                                <h1 className={healthChildPageTitle}>
                                    {getLocalized(hero.title, hLang)}
                                </h1>
                                <p className={healthChildPageLead}>
                                    {getLocalized(hero.subtitle, hLang)}
                                </p>
                                <div className="flex w-full flex-col gap-3 pt-2 lg:flex-row lg:flex-wrap lg:items-center lg:gap-4">
                                    <Link
                                        to={localePath(
                                            (heroCtaPrimary?.href as string) ??
                                                "/contact-us",
                                            hLang
                                        )}
                                        className={`${healthPrimaryButton} ${healthFocusVisible}`}
                                    >
                                        {getLocalized(heroCtaPrimary?.label, hLang)}
                                    </Link>
                                    <a
                                        href={
                                            (heroCtaSecondary?.href as string) ??
                                            "#modules"
                                        }
                                        onClick={(event) => {
                                            const href =
                                                (heroCtaSecondary?.href as string) ??
                                                "#modules";
                                            scrollToHealthHashAnchor(
                                                href,
                                                event,
                                                true,
                                                (to, options) => navigate(to, options)
                                            );
                                        }}
                                        className={`${healthSecondaryButton} ${healthFocusVisible}`}
                                    >
                                        <span className={healthButtonLabel}>
                                            {getLocalized(
                                                heroCtaSecondary?.label,
                                                hLang
                                            )}
                                        </span>
                                        <FontAwesomeIcon
                                            icon={faArrowRight}
                                            className={healthButtonArrowFa}
                                        />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <NavTree />

            <HealthWhyPatientSection data={whyPatient} />

            <HealthPatientJourneySection
                data={patientJourney}
                moduleTitleById={moduleTitleById}
            />

            <HealthPatientModulesSection
                data={modules}
                clinicalInputsLabel={clinicalInputsLabel}
                outputsLabel={outputsLabel}
                patientCareNoteLabel={patientCareNoteLabel}
                workflowBenefitLabel={workflowBenefitLabel}
            />

            <HealthIntegrationEcosystemSection
                data={integration}
                cardLayout="preview"
            />

            <HealthSection
                id="faq"
                ariaLabelledBy="faq-title"
                compact
                style={{ backgroundColor: "#ffffff" }}
            >
                <HealthSectionHeader
                    eyebrow={getLocalized(faq.eyebrow, hLang)}
                    title={getLocalized(faq.title, hLang)}
                    titleId="faq-title"
                />
                <div className={`${healthAimmaDoctorSectionContentOffset} w-full min-w-0`}>
                    {(faq.items as DataKeys[]).map((item, index) => (
                        <HealthFaqItem
                            key={index}
                            question={item.question}
                            answer={item.answer}
                            lang={hLang}
                        />
                    ))}
                </div>
            </HealthSection>

            <HealthSection
                id="cta"
                compact
                style={{ backgroundColor: HEALTH_CTA_BAND_BG }}
            >
                <div className={healthCtaBand}>
                    <div className="flex w-full min-w-0 flex-col gap-8 text-left lg:items-center lg:text-center">
                        <div className="flex w-full min-w-0 flex-col gap-2">
                            <h2 className={healthCtaTitle}>
                                {getLocalized(cta.title, hLang)}
                            </h2>
                            <p className={healthCtaSubtitle}>
                                {getLocalized(cta.subtitle, hLang)}
                            </p>
                        </div>
                        <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:justify-center">
                            <Link
                                to={localePath(cta.primaryHref as string, hLang)}
                                className={`${healthCtaButton} ${healthFocusVisible}`}
                            >
                                {getLocalized(cta.primaryLabel, hLang)}
                            </Link>
                            <Link
                                to={localePath(cta.secondaryHref as string, hLang)}
                                className={`${healthCtaSecondaryButton} ${healthFocusVisible}`}
                            >
                                {getLocalized(cta.secondaryLabel, hLang)}
                            </Link>
                        </div>
                    </div>
                </div>
            </HealthSection>
        </main>
    );
}
